import Icon from "../../common/components/Icon/Icon";
import { useNavigate } from "react-router-dom";
import { TrashIcon, SaveAsIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { FloatingTooltip } from "../components/Tooltip/FloatingTooltip";
import {
  setCurrentProjectFromId,
  setEdited as setEditedProject,
} from "src/features/projects/state/projectsSlice";
import { useAppDispatch } from "../../common/state/hooks";
import type { IMedia } from "../../model/unityObject";
import type { IProjectGroupWithProjects } from "src/features/projectGroups/state/projectGroupsSlice";
import type { ProjectGroupListItem } from "src/pages/ProjectGroups/ModeListProjectAndProjectsGroup";

const isMediaItem = (item: ProjectGroupListItem): item is IMedia => "filename" in item;
const isProjectGroupItem = (item: ProjectGroupListItem): item is IProjectGroupWithProjects =>
  "auth_type" in item;

interface IDisplayActions {
  handleClick?: (e: unknown) => void;
  itemsForList: ProjectGroupListItem;
  deleteOneFromList?: (id: number) => void;
  isProjectList?: boolean;
  openMediaDetail?: (id: number, item: unknown, index: number) => void;
  onDeleteMedia?: (media: IMedia) => void;
  isDeleteDisabled?: boolean;
  deleteDisabledTooltip?: string;
  isModalEdit?: boolean;
  isMediaList?: boolean;
  setEditedProjectGroupId?: (id: number, projectGroup: IProjectGroupWithProjects) => void;
}

const DisplayActions = ({
  itemsForList,
  deleteOneFromList = () => [],
  isProjectList,
  openMediaDetail = () => [],
  onDeleteMedia = () => [],
  isModalEdit,
  isMediaList,
  isDeleteDisabled = false,
  deleteDisabledTooltip = "",
  setEditedProjectGroupId = () => [],
}: IDisplayActions) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const deleteCourseLabel = deleteDisabledTooltip
    ? deleteDisabledTooltip
    : t("tooltip.deleteCourse");
  const deleteModuleLabel = deleteDisabledTooltip
    ? deleteDisabledTooltip
    : t("tooltip.deleteModule");
  let buttonsDynamic = (
    <>
      <FloatingTooltip content={t("tooltip.modifyCourse")} ariaHidden>
        <button
          type="button"
          aria-label={t("tooltip.modifyCourse")}
          onClick={(e) => {
            e.stopPropagation();
            if (isProjectGroupItem(itemsForList)) {
              setEditedProjectGroupId(itemsForList.id, itemsForList);
            }
            navigate(`/courses/${itemsForList.id}/edit`);
          }}
        >
          <Icon icon={"PencilAltIcon"} className={"w-6 h-6 mr-2 stroke-green-500"} />
        </button>
      </FloatingTooltip>
      <FloatingTooltip
        content={deleteCourseLabel}
        ariaHidden
        wrap
        wrapperClassName={isDeleteDisabled ? "cursor-not-allowed" : ""}
      >
        <button
          aria-label={deleteCourseLabel}
          onClick={(e) => {
            e.stopPropagation();
            deleteOneFromList(itemsForList?.id);
          }}
          disabled={isDeleteDisabled}
          className={`${isDeleteDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
        >
          <TrashIcon
            className={`w-6 h-6 mr-3 ${isDeleteDisabled ? "stroke-gray-500" : "stroke-red-500"}`}
          />
        </button>
      </FloatingTooltip>
    </>
  );
  if (isProjectList) {
    buttonsDynamic = (
      <>
        <FloatingTooltip content={t("tooltip.modifyModule")} ariaHidden>
          <button
            className="mr-2 cursor-pointer"
            aria-label={t("tooltip.modifyModule")}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(setCurrentProjectFromId(Number(itemsForList.id)));
              dispatch(setEditedProject());
              if (isProjectGroupItem(itemsForList)) {
                setEditedProjectGroupId(itemsForList.id, itemsForList);
              }
              navigate(`../list/${itemsForList.id}/edit`);
            }}
          >
            <Icon icon={"PencilAltIcon"} className="w-6 h-6 stroke-green-500" />
          </button>
        </FloatingTooltip>

        <FloatingTooltip content={t("tooltip.faitTemplate")} ariaHidden>
          <button
            aria-label={t("tooltip.faitTemplate")}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`../template/${itemsForList?.id}`);
            }}
          >
            <SaveAsIcon className="w-6 h-6 stroke-green-500 mr-2 mb-1" />
          </button>
        </FloatingTooltip>

        <FloatingTooltip
          content={deleteModuleLabel}
          ariaHidden
          wrap
          wrapperClassName={isDeleteDisabled ? "cursor-not-allowed" : ""}
        >
          <button
            aria-label={deleteModuleLabel}
            onClick={(e) => {
              e.stopPropagation();
              deleteOneFromList(itemsForList?.id);
            }}
            disabled={isDeleteDisabled}
            className={`${isDeleteDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            <TrashIcon
              className={`w-6 h-6 mr-3 ${isDeleteDisabled ? "stroke-gray-500" : "stroke-red-500"}`}
            />
          </button>
        </FloatingTooltip>
      </>
    );
  }

  const ActionButtons = () => {
    if (isMediaList) {
      if (!isMediaItem(itemsForList)) {
        return <></>;
      }
      if (itemsForList.organization?.id !== 3) {
        return (
          <div className="flex">
            <FloatingTooltip content={t("tooltip.editMedia")} ariaHidden>
              <button
                className="mr-2"
                aria-label={t("tooltip.editMedia")}
                onClick={(e) => {
                  e.stopPropagation();
                  openMediaDetail(itemsForList.id, 0, 0);
                }}
              >
                <Icon icon={"PencilAltIcon"} className={" w-6 h-6 stroke-green-500"} />
              </button>
            </FloatingTooltip>
            {isModalEdit && (
              <FloatingTooltip content={t("tooltip.deleteMedia")} ariaHidden>
                <button
                  aria-label={t("tooltip.deleteMedia")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMedia(itemsForList);
                  }}
                >
                  <TrashIcon className="w-6 h-6 stroke-red-500 mr-3" />
                </button>
              </FloatingTooltip>
            )}
          </div>
        );
      } else {
        return <></>;
      }
    } else {
      return <>{buttonsDynamic}</>;
    }
  };

  return (
    <div className="flex">
      <ActionButtons />
    </div>
  );
};

export default DisplayActions;
